import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CopyOutlined,
  EyeOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  SearchOutlined
} from '@ant-design/icons';
import moment from 'moment';
import styled, { css } from 'styled-components';

import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { PlainButton } from '../components/pageStyles';
import { ConfigEntity } from '../configStudio/configSubmission';
import { TC } from '../configStudio/kit/ConfigPanel';
import { LinkButton, tint, wz, wzType } from '../wizard/wizardStyles';
import {
  filterHistory,
  HISTORY_FILTERS,
  HISTORY_PAGE_SIZE,
  HistoryFilter,
  historyRecordRoute,
  HistoryRow,
  HistoryStatus
} from './historyModel';

const TH = `${TC}.history`;

// 67: Campaign blue, Rule amber, Reward violet, Audience green; Draft / Ended grey, Live green,
// In review amber.
const TYPE_INK: Record<ConfigEntity, string> = {
  campaign: wz.accent,
  rule: wz.highlight,
  reward: wz.violet,
  audience: wz.success
};

const STATUS_INK: Record<HistoryStatus, string> = {
  draft: wz.muted,
  live: wz.success,
  in_review: wz.highlight,
  ended: wz.muted
};

// 6985:9564: the 940 panel, r16.
const Panel = styled.section`
  width: 100%;
  max-width: 940px;
  margin: 0 auto;
  padding: 26px 30px 30px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 16px;
  background: ${wz.panel};
`;

const Head = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h1`
  margin: 0;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.title}
`;

const Subtitle = styled.p`
  margin: 22px 0 0;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.question}
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 18px;

  > * + * {
    margin-left: 11px;
  }
`;

// 6985:9577 `hsearch`: 380 x 42, r9.
const Search = styled.label`
  display: inline-flex;
  align-items: center;
  width: 380px;
  height: 42px;
  padding: 0 14px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 9px;
  background: ${wz.field};
  color: ${wz.muted};

  > input {
    flex: 1 1 auto;
    min-width: 0;
    margin-left: 10px;
    border: 0;
    outline: 0;
    background: transparent;
    color: ${wz.text};
    ${wzType.label}
  }

  > input::placeholder {
    color: ${wz.placeholder};
  }

  &:focus-within {
    border-color: ${wz.accent};
  }
`;

// 6985:9582: 34-tall pills, the picked one tinted in the accent.
const Chip = styled(PlainButton)<{ $on: boolean }>`
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  font-weight: 600;
  ${wzType.label}
  ${({ $on }) =>
    $on
      ? css`
          border: 1px solid ${wz.accent};
          background: ${tint(wz.accent, 0.14)};
          color: ${wz.accent};
        `
      : css`
          border: 1px solid ${wz.border};
          background: ${wz.field};
          color: ${wz.secondary};
        `}
`;

// Below the drawn 878 the table scrolls sideways on the custom bar, never the OS one.
const TableScroll = styled.div`
  margin-top: 20px;
  overflow-x: auto;
  ${intelligenceScrollbar('x')}
`;

// 6985:9588 `htable`: r12; columns 242 / 124 / 128 / 148 / 142 / 92, a 43-tall header.
const Table = styled.table`
  width: 100%;
  min-width: 876px;
  overflow: hidden;
  border: ${wz.line} solid ${wz.border};
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 12px;
  table-layout: fixed;

  th {
    height: 43px;
    padding: 0 14px;
    background: ${wz.field};
    color: ${wz.muted};
    font-weight: 800;
    text-align: left;
    text-transform: uppercase;
    ${wzType.eyebrow}
  }

  td {
    padding: 14px;
    border-top: ${wz.line} solid ${wz.border};
    color: ${wz.secondary};
    vertical-align: middle;
    ${wzType.label}
  }
`;

const Name = styled.span`
  display: block;
  color: ${wz.text};
  font-weight: 600;
`;

const Reference = styled.span`
  display: block;
  margin-top: 6px;
  color: ${wz.muted};
  ${wzType.hint}
`;

const Tag = styled.span<{ $ink: string }>`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: ${({ $ink }) => tint($ink, 0.14)};
  color: ${({ $ink }) => $ink};
  font-size: 11px;
  font-weight: 700;
`;

const Actions = styled.span`
  display: inline-flex;
  align-items: center;

  > * + * {
    margin-left: 12px;
  }

  a,
  button {
    color: ${wz.muted};
  }

  a:hover,
  button:hover {
    color: ${wz.text};
  }
`;

const Foot = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
  color: ${wz.muted};
  ${wzType.label}
`;

const Pager = styled.nav`
  display: flex;

  > * + * {
    margin-left: 8px;
  }
`;

const PageButton = styled(PlainButton)<{ $on?: boolean }>`
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid ${({ $on }) => ($on ? wz.accent : wz.border)};
  border-radius: 6px;
  color: ${({ $on }) => ($on ? wz.accent : wz.secondary)};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Empty = styled.td`
  text-align: center;
`;

interface IHistoryListProps {
  rows: HistoryRow[];
  /** "+ New configuration": back to the Config tab's entry. */
  onNew: () => void;
  /** The copy icon; no duplicate flow is drawn. */
  onDuplicate: (row: HistoryRow) => void;
}

/** 67 "History": everything configured, newest first; view opens the record's page. */
const HistoryList: React.FC<IHistoryListProps> = ({ rows, onNew, onDuplicate }) => {
  const { t } = useMultiLanguageTranslation();
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const shown = useMemo(() => filterHistory(rows, filter, search), [rows, filter, search]);
  const pages = Math.max(1, Math.ceil(shown.length / HISTORY_PAGE_SIZE));
  const current = Math.min(page, pages);
  const pageRows = shown.slice((current - 1) * HISTORY_PAGE_SIZE, current * HISTORY_PAGE_SIZE);

  return (
    <Panel data-testid="orbit-history">
      <Head>
        <Title>{t(`${TH}.title`)}</Title>
        <LinkButton type="button" onClick={onNew}>
          <PlusOutlined aria-hidden="true" style={{ fontSize: 11 }} />
          <span>{t(`${TH}.newConfiguration`)}</span>
        </LinkButton>
      </Head>
      <Subtitle>{t(`${TH}.subtitle`)}</Subtitle>
      <Toolbar>
        <Search>
          <SearchOutlined aria-hidden="true" />
          <input
            aria-label={t(`${TH}.search`)}
            placeholder={t(`${TH}.search`)}
            value={search}
            onChange={event => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </Search>
        {HISTORY_FILTERS.map(option => (
          <Chip
            key={option}
            type="button"
            aria-pressed={filter === option}
            $on={filter === option}
            onClick={() => {
              setFilter(option);
              setPage(1);
            }}
          >
            {t(`${TH}.types.${option}`)}
          </Chip>
        ))}
      </Toolbar>
      <TableScroll>
        <Table>
          <colgroup>
            {[242, 124, 128, 148, 142, 92].map(width => (
              <col key={width} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {(['name', 'type', 'status', 'createdBy', 'updated', 'actions'] as const).map(
                column => (
                  <th key={column} scope="col">
                    {t(`${TH}.columns.${column}`)}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <Empty colSpan={6}>{t(`${TH}.empty`)}</Empty>
              </tr>
            ) : (
              pageRows.map(row => (
                <tr key={row.reference} data-testid="orbit-history-row">
                  <td>
                    <Name data-testid="orbit-history-name">{row.name}</Name>
                    <Reference>{row.reference}</Reference>
                  </td>
                  <td>
                    <Tag $ink={TYPE_INK[row.type]}>{t(`${TH}.type.${row.type}`)}</Tag>
                  </td>
                  <td>
                    <Tag $ink={STATUS_INK[row.status]}>{t(`${TH}.status.${row.status}`)}</Tag>
                  </td>
                  <td>{row.createdBy}</td>
                  <td>
                    {moment.parseZone(row.updatedAt).format('DD MMM YYYY')}
                    <br />
                    {moment.parseZone(row.updatedAt).format('HH:mm')}
                  </td>
                  <td>
                    <Actions>
                      <Link
                        to={historyRecordRoute(row)}
                        aria-label={t(`${TH}.view`, { name: row.name })}
                      >
                        <EyeOutlined aria-hidden="true" />
                      </Link>
                      <PlainButton
                        type="button"
                        aria-label={t(`${TH}.duplicate`, { name: row.name })}
                        onClick={() => onDuplicate(row)}
                      >
                        <CopyOutlined aria-hidden="true" />
                      </PlainButton>
                    </Actions>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableScroll>
      <Foot>
        <span>{t(`${TH}.showing`, { shown: pageRows.length, total: shown.length })}</span>
        <Pager aria-label={t(`${TH}.title`)}>
          <PageButton
            type="button"
            aria-label={t(`${TH}.previous`)}
            disabled={current === 1}
            onClick={() => setPage(current - 1)}
          >
            <LeftOutlined />
          </PageButton>
          {Array.from({ length: pages }, (_, index) => index + 1).map(number => (
            <PageButton
              key={number}
              type="button"
              $on={number === current}
              aria-current={number === current ? 'page' : undefined}
              aria-label={t(`${TH}.page`, { page: number })}
              onClick={() => setPage(number)}
            >
              {number}
            </PageButton>
          ))}
          <PageButton
            type="button"
            aria-label={t(`${TH}.nextPage`)}
            disabled={current === pages}
            onClick={() => setPage(current + 1)}
          >
            <RightOutlined />
          </PageButton>
        </Pager>
      </Foot>
    </Panel>
  );
};

export default HistoryList;
